import { AbstractControl } from '@angular/forms';
import * as moment from 'moment';
import 'moment-timezone';
import { filter } from 'rxjs';

/**
 *  Patrones simples y reutilizables
 *  — letras, números, espacio y .,'-  (máx. 160 car.)
 *  — hora “HH:mm”
 */
export const PAT_ADDR   = /^[\p{L}\d\s.,'-]{1,160}$/u;
export const PAT_V_TEXT = /^[\p{L}\d\s.'-]{1,30}$/u;
export const PAT_TIME   = /^([01]\d|2[0-3]):[0-5]\d$/

export function formatDate(date: string): string {
  if (!date) {
    return '';
  }
  const longDateFormat = /^[A-Za-z]+,\s+\d{2}\s+[A-Za-z]+\s+\d{4}$/;
  if (longDateFormat.test(date.trim())) {
    return (moment as any)(date.trim(), 'dddd, DD MMM YYYY').format('DD/MM/YYYY');
  }
  // Extraer solo YYYY-MM-DD para evitar conversión de timezone local
  const cleanDate = date.split(' ')[0].split('T')[0];
  return (moment as any)(cleanDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
}

export function formatHour(time: string): string {
  if (!time) {
    return '';
  }
  const [hour, minute] = time.split(':');
  return `${hour}:${minute}`;
}

// Método que convierte los segundos (como string) a una hora (formateada en HH:mm)
// *Nota:* El frontend simplemente convierte los segundos a la hora sin hacer ajustes adicionales.
export function formatSecondsToHourAndMinutes(
  seconds?: string | number | null
): string {
  const total = typeof seconds === 'string' ? Number(seconds) : seconds;

  if (total == null || isNaN(total)) return '';

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
}

export function formatSecondsToCompleteDate(
  seconds: string | undefined
): string {
  if (!seconds) return '';

  const date = new Date(Number(seconds) * 1000);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
}

export function formatCompleteStringDateToHourAndMinutes(
  completeDate: string
): string {
  if (!completeDate) return '00:00';

  const date = moment.utc(completeDate.replace(' UTC', '')); // quitamos ' UTC' y tratamos como UTC
  return date.format('HH:mm');
}

// Convierte "2025-03-25T10:16:10.1552056Z UTC" → "DD/MM/YYYY"
export function formatCompleteStringDatetoCompleteDateFormated(
  completeDate: string
): string {
  if (!completeDate) return '';

  const date = moment.utc(completeDate.replace(' UTC', ''));
  return date.format('DD/MM/YYYY');
}
export function extractTimeWithoutSeconds(assignedDate: string): string {
  if (!assignedDate) return '';

  const datePart = assignedDate.split(' ')[0];
  const date = new Date(datePart);

  if (isNaN(date.getTime())) return '';

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function formatDateFromAssignedDate(assignedDate: string): string {
  if (!assignedDate) return '';

  const datePart = assignedDate.split(' ')[0];
  const date = new Date(datePart);

  if (isNaN(date.getTime())) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

enum OrderStatus {
  INITIALIZED = 0,
  PENDING = 1,
  CONFIRMED = 2,
  PICKING_UP = 3,
  IN_PROGRESS = 4,
  DELIVERING = 5,
  FINISHED = 6,
  FAILED = 7,
  CANCELLED_BY_CAFLER = 10,
  CANCELLED_BY_CLIENT = 11,
}

export function getOrderStatusName(statusId: number): string {
  for (const [status, id] of Object.entries(OrderStatus)) {
    if (id === statusId) {
      return status;
    }
  }
  return 'UNKNOWN_STATUS';
}

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
export const hhmmPattern = /^([01]\d|2[0-3]):[0-5]\d$/;

export function hhmmValidator(control: AbstractControl) {
  return hhmmPattern.test(control.value) ? null : { invalidTime: true };
}

export function nonNull<T>() {
  return filter<T, NonNullable<T>>(
    (x): x is NonNullable<T> => x !== null && x !== undefined
  );
}

export function humanizeDuration(
  seconds: number | string,
  conjunction: string = 'y'
): string {
  const total = typeof seconds === 'string' ? +seconds : seconds;
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);

  if (hours && minutes) {
    return `${hours}h ${conjunction} ${minutes}min`;
  }
  if (hours) return `${hours}h`;
  return `${minutes} min`;
}

/**
 * Convierte el “completeDate” gRPC en un `Date` LOCAL
 * preservando hora y minutos tal y como vienen.
 */
export function parseGrpcLocal(
  raw?: string | { completeDate?: string | null } | null
): Date | null {
  if (!raw) return null;

  const str =
    typeof raw === 'string' ? raw : raw.completeDate ?? '';
  if (!str) return null;

  const iso = str.match(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?/
  )?.[0];
  if (!iso) return null;

  const dt = new Date(iso);
  return isNaN(dt.getTime()) ? null : dt;
}

/** dd/mm/yyyy */
export function formatDateISO(date: Date): string {
  return date
    ? `${date.getDate().toString().padStart(2, '0')}/` +
        `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
        date.getFullYear()
    : '–';
}

/** HH:mm */
export function formatHourISO(date: Date): string {
  return date
    ? `${date.getHours().toString().padStart(2, '0')}:` +
        date.getMinutes().toString().padStart(2, '0')
    : '–';
}